<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Virtual tours can rank on Google — but only if you optimise them
            correctly. Most virtual tour creators publish a tour and expect it
            to be discovered organically. Without proper SEO, your tour exists
            in a digital vacuum. This guide covers every technical and content
            SEO action you can take to rank your virtual tours higher on Google
            in 2026.
          </p>
        </section>

        <section class="key-takeaways">
          <h2>Key Takeaways</h2>
          <ul>
            <li>Virtual tours improve SEO through increased dwell time, lower bounce rates, and rich media signals.</li>
            <li>Add BlogPosting and VirtualLocation schema markup to help Google understand your tour pages.</li>
            <li>Optimize tour embed pages with descriptive titles, alt text, and location-based keywords.</li>
            <li>Internal linking between tours and related content strengthens topical authority.</li>
            <li>Google Business Profile tours directly boost local pack rankings.</li>
          </ul>
        </section>

        <section>
          <h2>1. Create a Dedicated Landing Page for Each Tour</h2>
          <p>
            Don't just embed your tour and call it done. Every tour needs a
            dedicated SEO-optimised landing page with: a keyword-targeted H1, at
            least 500 words of original descriptive content about the property
            or location, proper meta title and description, and the tour embed
            above the fold. This page is what Google indexes — the tour itself
            is just interactive content within it.
          </p>
        </section>

        <section>
          <h2>2. Use Schema Markup for Virtual Tours</h2>
          <p>
            Add <code>TouristAttraction</code> or
            <code>LocalBusiness</code> schema to virtual tour landing pages. For
            real estate listings, use <code>RealEstateListing</code> schema. For
            business virtual tours, use <code>LocalBusiness</code> schema with a
            <code>hasMap</code> property pointing to your Google Maps listing.
            These structured data types help Google understand what your tour is
            about.
          </p>
        </section>

        <section>
          <h2>3. Optimise for Local Keywords</h2>
          <p>
            Virtual tours are inherently local. Include location-specific
            keywords: "[City] virtual tour," "[Neighbourhood] property tour,"
            "[Street] real estate virtual tour." Use Google's local pack
            keywords naturally in your landing page content. Geo-tag the page
            using the <code>geo.position</code> and <code>ICBM</code> meta tags
            with your property coordinates.
          </p>
        </section>

        <section>
          <h2>4. Build Backlinks from Local Directories</h2>
          <p>
            Submit your virtual tour landing pages to local business
            directories, real estate portals, and tourism websites. Each
            backlink signals local relevance to Google. Key directories: Google
            Business Profile, Bing Places, Yelp, TripAdvisor (for hospitality),
            Zillow/Rightmove (for real estate).
          </p>
        </section>

        <section>
          <h2>5. Optimise Page Speed</h2>
          <p>
            Virtual tours are data-heavy. Use lazy loading for the tour iframe —
            don't load it until the user scrolls to it. This dramatically
            reduces initial page load time and improves Core Web Vitals scores,
            which directly impact Google ranking.
          </p>
        </section>

        <section>
          <h2>6. Add the Tour to Your Google Business Profile</h2>
          <p>
            Google Business Profile virtual tours appear directly in Search and
            Maps results. This is the single highest-leverage SEO action for
            local business virtual tours — it puts your tour in front of people
            actively searching for your business type in your area.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/360-photos-local-seo">360 Photos Improve Local SEO</a></li>
          <li><a href="/blog/en/google-business-virtual-tour">Google Business Virtual Tour</a></li>
          <li><a href="/blog/en/google-street-view-virtual-tour">Publish to Google Street View</a></li>
          <li><a href="/blog/en/virtual-tour-analytics">Track Virtual Tour Analytics</a></li>
          <li><a href="/blog/en/embed-360-virtual-tour-website">Embed a Tour on Your Website</a></li>
          <li><a href="/blog/en/virtual-tour-lead-generation">Convert Tour Viewers into Leads</a></li>
          <li><a href="/blog/en/virtual-tour-google-business-profile">Optimize Google Business Profile</a></li>
          <li><a href="/blog/en/embed-virtual-tour-wordpress">Embed Tours in WordPress</a></li>
          </ul>
        </section>


        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find((p) => p.slug === "virtual-tour-seo-guide");

useBlogSeo({
  title: "Virtual Tour SEO: Rank on Google [2026 Guide] | Travvir",
  description:
    "How to rank your virtual tours on Google in 2026. Covers schema markup, page speed, image optimization, local SEO, and link building for 360° content.",
  keywords:
    "virtual tour SEO, rank virtual tour google, 360 tour SEO, virtual tour search engine optimization, SEO for virtual tours",
  slug: "virtual-tour-seo-guide",
  image: postData.image,
  postData,
});
</script>
